/**
 * qrCode — locally-bundled SVG QR generator.
 *
 * Bill 2026-05-10 — Phase 1 (v0.14.17 bundle).
 *
 * Wraps the `qrcode` npm package (MIT, v1.5.x — bundled locally, NO CDN).
 * The dispatcher uses this to render the "open on your phone" QR fallback
 * when a drawn-required document loads on a non-touch desktop.
 *
 * Output is SVG markup (string) so the consumer can drop it into a div
 * via `dangerouslySetInnerHTML` without any imperative DOM work or canvas
 * fallback. Tokenized signing URLs typically fit in QR version 6-8 at
 * error-correction level M — well within `qrcode`'s default capacity.
 *
 * CONTRACT(sibling-primitives-agent): if the primitives agent later
 * ships its own generator, this file can be replaced with a re-export.
 */

import QRCode from 'qrcode';

export interface GenerateQrOptions {
  /** Pixel size of the rendered SVG (square). Default 200. */
  size?: number;
  /** SVG quiet-zone (margin in modules). Default 2. */
  margin?: number;
  /** Foreground color. Default PCB primary navy '#1a406a'. */
  dark?: string;
  /** Background color. Default white. */
  light?: string;
}

/**
 * Build an SVG QR for the given text (commonly a tokenized URL).
 * Returns the markup as a string for `dangerouslySetInnerHTML`.
 */
export async function generateQrSvg(
  text: string,
  opts: GenerateQrOptions = {}
): Promise<string> {
  const size = opts.size ?? 200;
  const margin = opts.margin ?? 2;
  const dark = opts.dark ?? '#1a406a';
  const light = opts.light ?? '#ffffff';
  const svg = await QRCode.toString(text, {
    type: 'svg',
    errorCorrectionLevel: 'M',
    margin,
    color: { dark, light },
    width: size,
  });
  return svg;
}
